import { useState } from 'react';
import { apiConfig } from './config/api';

function App() {
  const [userName, setUserName] = useState('');
  const [postContent, setPostContent] = useState('');
  const [likesContent, setLikesContent] = useState('');
  const [parsedUsers, setParsedUsers] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [postAuthor, setPostAuthor] = useState('');
  const [postAuthorTitle, setPostAuthorTitle] = useState('');
  const [postContentParsed, setPostContentParsed] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [generatingIndex, setGeneratingIndex] = useState(null);
  const [showDelayMessage, setShowDelayMessage] = useState(false);
  const [delayTimeoutId, setDelayTimeoutId] = useState(null);
  const [showParsingDelayMessage, setShowParsingDelayMessage] = useState(false);
  const [parsingDelayTimeoutId, setParsingDelayTimeoutId] = useState(null);
  const [activeTab, setActiveTab] = useState('post'); // 'post' or 'likes'

  const parseEngagement = async () => {
    if (!postContent.trim() || !likesContent.trim()) {
      alert('Bitte fügen Sie sowohl den Post-Inhalt als auch die Likes-Daten ein');
      return;
    }

    setLoading(true);
    setShowParsingDelayMessage(false);
    
    // Show delay message after 3 seconds
    const timeoutId = setTimeout(() => {
      setShowParsingDelayMessage(true);
    }, 3000);
    setParsingDelayTimeoutId(timeoutId);
    try {
      // Verwende die API-Konfiguration!
      const response = await fetch(apiConfig.endpoints.parseSeparate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          postContent: postContent,
          likesContent: likesContent
        })
      });

      if (response.ok) {
        const postAnalysis = await response.json();
        
        setPostAuthor(postAnalysis.authorName);
        setPostAuthorTitle(postAnalysis.authorTitle);
        setPostContentParsed(postAnalysis.postContent);
        setParsedUsers(postAnalysis.engagedUsers);

        const leads = postAnalysis.engagedUsers.filter(user => 
          user.seniorityLevel && 
          (user.seniorityLevel === 'Executive' || 
           user.seniorityLevel === 'Senior' ||
           user.jobTitle.toLowerCase().includes('manager'))
        );
        setFilteredLeads(leads);
      } else {
        // Handle HTTP error responses
        const errorText = await response.text();
        console.error('HTTP Error:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorBody: errorText
        });
        alert(`Fehler ${response.status}: ${response.statusText}. Details in der Konsole.`);
      }
    } catch (error) {
      // Handle network errors, JSON parsing errors, etc.
      console.error('Request Error Details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        apiUrl: apiConfig.endpoints.parseSeparate,
        timestamp: new Date().toISOString()
      });
      alert(`Fehler beim Parsen der Daten: ${error.message}. Details in der Konsole.`);
    }
    
    // Clear delay message and timeout
    if (parsingDelayTimeoutId) {
      clearTimeout(parsingDelayTimeoutId);
      setParsingDelayTimeoutId(null);
    }
    setShowParsingDelayMessage(false);
    setLoading(false);
  };

  const generateMessage = async (user, index) => {
    if (!userName.trim()) {
      alert('Bitte geben Sie zuerst Ihren Namen ein');
      return;
    }

    setGeneratingIndex(index);
    setSelectedUser(user);
    setShowDelayMessage(false);
    
    // Show delay message after 2 seconds
    const timeoutId = setTimeout(() => {
      setShowDelayMessage(true);
    }, 2000);
    setDelayTimeoutId(timeoutId);

    try {
      // Verwende die API-Konfiguration!
      const response = await fetch(apiConfig.endpoints.generateMessage, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: userName,
          targetName: user.name,
          targetJob: user.jobTitle,
          targetCompany: user.company,
          postContent: postContentParsed,
          postAuthor: postAuthor,
          postAuthorTitle: postAuthorTitle
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessageTemplate(result.message);
      } else {
        // Handle HTTP error responses
        const errorText = await response.text();
        console.error('HTTP Error (Generate Message):', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorBody: errorText
        });
        alert(`Fehler ${response.status}: ${response.statusText}. Details in der Konsole.`);
      }
    } catch (error) {
      // Handle network errors, JSON parsing errors, etc.
      console.error('Generate Message Error Details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        apiUrl: apiConfig.endpoints.generateMessage,
        timestamp: new Date().toISOString()
      });
      alert(`Fehler beim Generieren der Nachricht: ${error.message}. Details in der Konsole.`);
    }
    
    // Clear delay message and timeout
    if (delayTimeoutId) {
      clearTimeout(delayTimeoutId);
      setDelayTimeoutId(null);
    }
    setShowDelayMessage(false);
    setGeneratingIndex(null);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(messageTemplate);
    alert('Nachricht in Zwischenablage kopiert');
  };

  const styles = {
    app: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0A66C2 0%, #004182 25%, #00344D 50%, #001E2B 75%, #000F1A 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
      color: '#ffffff',
      overflow: 'hidden'
    },
    container: {
      minHeight: '100vh',
      overflow: 'auto',
      scrollBehavior: 'smooth'
    },
    maxWidth: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 24px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '60px'
    },
    title: {
      fontSize: 'clamp(2.5rem, 5vw, 4rem)',
      fontWeight: '900',
      marginBottom: '16px',
      background: 'linear-gradient(135deg, #ffffff 0%, #0A66C2 50%, #ffffff 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textShadow: '0 0 40px rgba(10, 102, 194, 0.3)',
      letterSpacing: '-0.02em'
    },
    subtitle: {
      fontSize: '1.25rem',
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: '400',
      letterSpacing: '0.02em'
    },
    configSection: {
      marginBottom: '60px'
    },
    card: {
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(24px)',
      borderRadius: '24px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '40px',
      marginBottom: '32px',
      boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    formGroup: {
      marginBottom: '32px'
    },
    label: {
      display: 'block',
      marginBottom: '12px',
      fontSize: '14px',
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.9)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em'
    },
    input: {
      width: '100%',
      padding: '20px 24px',
      background: 'rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      fontSize: '16px',
      color: '#ffffff',
      transition: 'all 0.3s ease',
      outline: 'none',
      boxSizing: 'border-box'
    },
    inputFocus: {
      borderColor: '#0A66C2',
      background: 'rgba(0, 0, 0, 0.4)',
      boxShadow: '0 0 0 4px rgba(10, 102, 194, 0.1), 0 8px 32px rgba(10, 102, 194, 0.2)',
      transform: 'translateY(-2px)'
    },
    textarea: {
      width: '100%',
      minHeight: '300px',
      padding: '20px 24px',
      background: 'rgba(0, 0, 0, 0.3)',
      border: '2px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      fontSize: '15px',
      color: '#ffffff',
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: '1.6',
      transition: 'all 0.3s ease',
      outline: 'none',
      boxSizing: 'border-box'
    },
    tabs: {
      display: 'flex',
      marginBottom: '24px',
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '12px',
      padding: '4px',
      gap: '4px'
    },
    tab: {
      flex: 1,
      padding: '12px 24px',
      background: 'transparent',
      color: 'rgba(255, 255, 255, 0.6)',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    activeTab: {
      background: 'rgba(10, 102, 194, 0.3)',
      color: '#ffffff',
      boxShadow: '0 2px 8px rgba(10, 102, 194, 0.3)'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '40px'
    },
    primaryButton: {
      padding: '18px 48px',
      background: 'linear-gradient(135deg, #0A66C2 0%, #004182 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '50px',
      fontSize: '16px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      boxShadow: '0 12px 40px rgba(10, 102, 194, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
      position: 'relative',
      overflow: 'hidden'
    },
    primaryButtonHover: {
      transform: 'translateY(-3px) scale(1.02)',
      boxShadow: '0 20px 60px rgba(10, 102, 194, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
    },
    primaryButtonLoading: {
      background: 'linear-gradient(135deg, rgba(10, 102, 194, 0.6) 0%, rgba(0, 65, 130, 0.6) 100%)',
      cursor: 'not-allowed',
      transform: 'none'
    },
    primaryButtonDisabled: {
      background: 'rgba(10, 102, 194, 0.3)',
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none'
    },
    secondaryButton: {
      padding: '12px 28px',
      background: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '25px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      marginRight: '16px',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(12px)'
    },
    successButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #57C4DC 0%, #0A66C2 100%)',
      color: '#ffffff',
      border: 'none',
      borderRadius: '25px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 24px rgba(87, 196, 220, 0.3)'
    },
    successButtonDisabled: {
      background: 'rgba(87, 196, 220, 0.3)',
      cursor: 'not-allowed',
      boxShadow: 'none'
    },
    postInfo: {
      background: 'rgba(10, 102, 194, 0.1)',
      padding: '24px',
      borderRadius: '16px',
      border: '1px solid rgba(10, 102, 194, 0.3)',
      marginBottom: '32px'
    },
    postInfoTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '12px'
    },
    postInfoDetail: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.8)',
      marginBottom: '8px'
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '40px'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(16px)',
      padding: '32px 24px',
      borderRadius: '20px',
      textAlign: 'center',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease'
    },
    statNumber: {
      fontSize: '3rem',
      fontWeight: '900',
      color: '#0A66C2',
      marginBottom: '8px',
      textShadow: '0 0 20px rgba(10, 102, 194, 0.5)'
    },
    statLabel: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: '600'
    },
    resultsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '32px',
      marginBottom: '40px'
    },
    userList: {
      maxHeight: '500px',
      overflowY: 'auto',
      paddingRight: '8px',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(10, 102, 194, 0.5) transparent'
    },
    userCard: {
      padding: '24px',
      background: 'rgba(0, 0, 0, 0.2)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      marginBottom: '16px',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(8px)'
    },
    leadCard: {
      padding: '24px',
      background: 'rgba(10, 102, 194, 0.15)',
      border: '1px solid rgba(10, 102, 194, 0.3)',
      borderRadius: '16px',
      marginBottom: '16px',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(8px)'
    },
    userName: {
      margin: '0 0 16px 0',
      fontSize: '18px',
      fontWeight: '700',
      color: '#ffffff'
    },
    userDetail: {
      margin: '8px 0',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.8)',
      lineHeight: '1.5'
    },
    userDetailLabel: {
      color: '#0A66C2',
      fontWeight: '600',
      marginRight: '8px'
    },
    messageCard: {
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(24px)',
      borderRadius: '24px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '40px',
      boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.4)',
      marginTop: '40px'
    },
    messageTextarea: {
      width: '100%',
      minHeight: '200px',
      padding: '24px',
      background: 'rgba(0, 0, 0, 0.4)',
      border: '2px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      fontSize: '15px',
      color: '#ffffff',
      resize: 'vertical',
      fontFamily: 'inherit',
      lineHeight: '1.7',
      outline: 'none',
      boxSizing: 'border-box'
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      marginTop: '32px'
    },
    helperText: {
      fontSize: '13px',
      color: 'rgba(255, 255, 255, 0.6)',
      marginTop: '8px',
      fontStyle: 'italic'
    },
    delayMessage: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)',
      marginTop: '8px',
      textAlign: 'center',
      fontStyle: 'italic',
      background: 'rgba(10, 102, 194, 0.1)',
      padding: '8px 12px',
      borderRadius: '8px',
      border: '1px solid rgba(10, 102, 194, 0.2)'
    }
  };

   return (
    <div style={styles.app}>
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <h1 style={styles.title}>LinkedIn Analyzer</h1>
            <p style={styles.subtitle}>PoC für LinkedIn Posts Analyse & Nachrichtengenerierung von Muhamed Nur Beck</p>
          </div>
          
          {/* Configuration Section */}
          <div style={styles.configSection}>
            <div style={styles.card}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Ihr Name</label>
                <input
                  type="text"
                  placeholder="Geben Sie Ihren vollständigen Namen ein"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  style={styles.input}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => Object.assign(e.target.style, styles.input)}
                />
              </div>
              
              {/* Tabs für Post-Content und Likes */}
              <div style={styles.tabs}>
                <button
                  style={{...styles.tab, ...(activeTab === 'post' ? styles.activeTab : {})}}
                  onClick={() => setActiveTab('post')}
                >
                  Post-Inhalt
                </button>
                <button
                  style={{...styles.tab, ...(activeTab === 'likes' ? styles.activeTab : {})}}
                  onClick={() => setActiveTab('likes')}
                >
                  Likes-Daten
                </button>
              </div>
              
              {/* Post Content Input */}
              {activeTab === 'post' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>LinkedIn Post-Inhalt</label>
                  <textarea
                    placeholder="Fügen Sie hier den kompletten LinkedIn Post ein (inklusive Autor-Information und Post-Text)..."
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                    style={styles.textarea}
                    onFocus={(e) => Object.assign(e.target.style, {...styles.textarea, ...styles.inputFocus})}
                    onBlur={(e) => Object.assign(e.target.style, styles.textarea)}
                  />
                  <p style={styles.helperText}>
                    Kopieren Sie den gesamten Post vom Autor-Namen bis zum Ende des Post-Inhalts
                  </p>
                </div>
              )}
              
              {/* Likes Content Input */}
              {activeTab === 'likes' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>LinkedIn Likes-Daten</label>
                  <textarea
                    placeholder="Fügen Sie hier alle Likes ein (beginnend mit 'like' für jeden Nutzer)..."
                    value={likesContent}
                    onChange={(e) => setLikesContent(e.target.value)}
                    style={styles.textarea}
                    onFocus={(e) => Object.assign(e.target.style, {...styles.textarea, ...styles.inputFocus})}
                    onBlur={(e) => Object.assign(e.target.style, styles.textarea)}
                  />
                  <p style={styles.helperText}>
                    Kopieren Sie alle "like" Einträge mit den zugehörigen Nutzerinformationen
                  </p>
                </div>
              )}
              
              <div>
                <div style={styles.buttonContainer}>
                  <button 
                    onClick={parseEngagement}
                    disabled={loading || !postContent.trim() || !likesContent.trim()}
                    style={
                      loading || !postContent.trim() || !likesContent.trim() 
                        ? {...styles.primaryButton, ...styles.primaryButtonDisabled} 
                        : styles.primaryButton
                    }
                    onMouseEnter={(e) => {
                      if (!loading && postContent.trim() && likesContent.trim()) {
                        Object.assign(e.target.style, {...styles.primaryButton, ...styles.primaryButtonHover})
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && postContent.trim() && likesContent.trim()) {
                        Object.assign(e.target.style, styles.primaryButton)
                      }
                    }}
                  >
                    {loading ? 'Daten werden verarbeitet...' : 'Post analysieren'}
                  </button>
                </div>
                {loading && showParsingDelayMessage && (
                  <div style={{...styles.delayMessage, textAlign: 'center', marginTop: '16px'}}>
                    Das kann etwas dauern, da das Backend beim ersten Aufruf hochgefahren wird.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Post Information */}
          {postAuthor && (
            <div style={styles.postInfo}>
              <h3 style={styles.postInfoTitle}>Post-Analyse</h3>
              <p style={styles.postInfoDetail}>
                <span style={styles.userDetailLabel}>Autor:</span>
                {postAuthor}
              </p>
              <p style={styles.postInfoDetail}>
                <span style={styles.userDetailLabel}>Position:</span>
                {postAuthorTitle}
              </p>
              <p style={styles.postInfoDetail}>
                <span style={styles.userDetailLabel}>Inhalt-Vorschau:</span>
                {postContentParsed.substring(0, 200)}...
              </p>
            </div>
          )}

          {/* Statistics */}
          {parsedUsers.length > 0 && (
            <div style={styles.stats}>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{parsedUsers.length}</div>
                <div style={styles.statLabel}>Gesamte engagierte Nutzer</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statNumber}>{filteredLeads.length}</div>
                <div style={styles.statLabel}>Prioritäts-Leads</div>
              </div>
            </div>
          )}

          {/* Results */}
          {parsedUsers.length > 0 && (
            <div style={styles.resultsGrid}>
              <div style={styles.card}>
                <h3 style={{color: '#ffffff', fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px'}}>
                  Alle Nutzer ({parsedUsers.length})
                </h3>
                <div style={styles.userList}>
                  {parsedUsers.map((user, index) => (
                    <div key={index} style={styles.userCard}>
                      <h4 style={styles.userName}>{user.name}</h4>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Position:</span>
                        {user.jobTitle}
                      </p>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Unternehmen:</span>
                        {user.company}
                      </p>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Ebene:</span>
                        {user.seniorityLevel}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={{color: '#ffffff', fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px'}}>
                  Prioritäts-Leads ({filteredLeads.length})
                </h3>
                <div style={styles.userList}>
                  {filteredLeads.map((user, index) => (
                    <div key={index} style={styles.leadCard}>
                      <h4 style={styles.userName}>{user.name}</h4>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Position:</span>
                        {user.jobTitle}
                      </p>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Unternehmen:</span>
                        {user.company}
                      </p>
                      <p style={styles.userDetail}>
                        <span style={styles.userDetailLabel}>Ebene:</span>
                        {user.seniorityLevel}
                      </p>
                      <div>
                        <button
                          onClick={() => generateMessage(user, index)}
                          disabled={generatingIndex === index}
                          style={generatingIndex === index ? 
                            {...styles.successButton, ...styles.successButtonDisabled} : 
                            styles.successButton
                          }
                        >
                          {generatingIndex === index ? 'Wird generiert...' : 'Nachricht generieren'}
                        </button>
                        {generatingIndex === index && showDelayMessage && (
                          <div style={styles.delayMessage}>
                            Beim ersten Aufruf kann es etwas länger dauern, da das Backend hochgefahren wird.
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Message Template */}
          {selectedUser && messageTemplate && (
            <div style={styles.messageCard}>
              <h3 style={{color: '#ffffff', fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px'}}>
                KI-generierte Nachricht für {selectedUser.name}
              </h3>
              <textarea
                value={messageTemplate}
                onChange={(e) => setMessageTemplate(e.target.value)}
                style={styles.messageTextarea}
              />
              <div style={styles.buttonGroup}>
                <button onClick={copyToClipboard} style={styles.primaryButton}>
                  In Zwischenablage kopieren
                </button>
                <button 
                  onClick={() => {setSelectedUser(null); setMessageTemplate('');}}
                  style={styles.secondaryButton}
                >
                  Schließen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;